import {UserManagerApiError} from "@/lib/api/user-manager-api-error";
import { clearTokenData, getDecodedToken, getRefreshToken, getToken, LocalStorageKeys, storeTokenData } from "@/lib/token/token"
import { FIVE_MINUTES } from "@/utils/constants"

import {
  ApiError,
  INVALID_REQUEST_AUTH_REFRESH_RESPONSE,
  INVALID_TOKEN_AUTH_REFRESH_RESPONSE,
  InvalidRequestApiError, InvalidTokenApiError
} from "./api-error"

export interface RequestOptions extends Omit<RequestInit, "headers"> {
  headers?: Record<string, string>
  method?: string
}

type ResponseType = "json" | "text" | "blob" | "blobAndHeader"

type RefreshResponse = {
  token: string
  refresh_token?: string
  expires_in?: number
}

/** Refresh once the access token has burned through this share of its own lifetime. */
const TOKEN_REFRESH_THRESHOLD = 0.8

/** Floor on how often a refresh can be triggered, so a skewed clock cannot refresh on every request. */
const MINIMUM_REFRESH_INTERVAL = 30 * 1000

/** A refresh that never settles must reject its waiters rather than hang the application. */
export const REFRESH_TIMEOUT = 15 * 1000

/** Web Locks name used to elect a single refreshing tab. */
const REFRESH_LOCK_NAME = 'vf-token-refresh'

const buildRequestOptions = (
  options?: RequestOptions,
  token?: string
): RequestOptions => {
  const defaultHeaders: HeadersInit = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  return {
    ...options,
    headers: {
      ...defaultHeaders,
      ...(options?.headers ?? {}),
      ...(token ? { "vf-token": token } : {}),
    },
  };
};

const handleError = async (response: Response): Promise<void> => {
  const contentType = response.headers.get("Content-Type") || ""

  if (contentType.includes("application/json")) {
    const error = await response.json()

    // Only 2fa sends this
    if (error?.login !== undefined || error?.['2fa_complete'] !== undefined) {
      throw new UserManagerApiError(error.message, error.login, error['2fa_complete'] || false, response.status)
    }

    if (error?.data?.message) {
      throw new ApiError(error.data.message, response.status)
    }

    if (error?.detail) {
      throw new ApiError(error.detail, response.status)
    }

    throw new ApiError(JSON.stringify(error), response.status)
  }

  const errorMessage = await response.text()

  throw new ApiError(errorMessage, response.status)
}

const parseResponse = async (response: Response, responseType: ResponseType) => {
  if (responseType === 'blob') {
    return response.blob()
  }

  if (responseType === "blobAndHeader") {
    const blob = await response.blob();

    return {
      blob,
      headers: {
        contentDisposition: response.headers.get("Content-Disposition"),
        contentType: response.headers.get("Content-Type"),
      },
    };
  }

  if (responseType === "text") {
    return response.text()
  }

  return response.json()
}

const sendRequest = async (
  url: string,
  returnAggregateData: boolean = false,
  options?: RequestOptions,
  baseUrlOverride?: string,
  responseType: ResponseType = "json",
  sendTokenInHeader: boolean = true,
  isRetry: boolean = false
): Promise<any> => {
  await refreshTokenIfStale()

  const token = localStorage.getItem(LocalStorageKeys.TOKEN) || undefined
  const requestOptions = buildRequestOptions(options, sendTokenInHeader ? token : undefined)
  const baseUrl = baseUrlOverride ?? import.meta.env.VITE_API_URL
  const response = await fetch(`${baseUrl}${url}`, requestOptions)

  // An access token can expire mid-flight or while a tab is idle, so recover once before surfacing a 401.
  if (response.status === 401 && !isRetry && sendTokenInHeader && token !== undefined) {
    const isRefreshed = await refreshForRetry(token)

    if (isRefreshed) {
      return sendRequest(url, returnAggregateData, options, baseUrlOverride, responseType, sendTokenInHeader, true)
    }
  }

  if (!response.ok) {
    await handleError(response)

    return
  }

  const data = await parseResponse(response, responseType)

  if (responseType === "text") {
    return data
  }

  if (returnAggregateData && "data" in data && "aggregate_data" in data) {
    return { data: data.data, aggregate_data: data.aggregate_data }
  }

  if ("data" in data) {
    return data.data
  }

  return data
}

export const get = (url: string, returnAggregateData = false, baseUrlOverride?: string) => {
  return sendRequest(url, returnAggregateData, undefined, baseUrlOverride)
}

export const getCsvFile = (url: string) => {
  return sendRequest(url, false, undefined, undefined, "blob")
}

export const getFileWithHeaders = (url: string, baseUrlOverride?:string, sendTokenInHeader?: boolean) => {
  return sendRequest(url, false, undefined, baseUrlOverride, "blobAndHeader", sendTokenInHeader)
}

export const post = (url: string, data: object, baseUrlOverride?: string, sendTokenInHeader?: boolean, optionsOverride?: object) => {
  return sendRequest(url, false, {
    method: 'POST',
    body: JSON.stringify(data),
    ...optionsOverride
  }, baseUrlOverride, 'json', sendTokenInHeader)
}

export const put = (url: string, data: object, baseUrlOverride?: string, sendTokenInHeader?: boolean, optionsOverride?: object) => {
  return sendRequest(url, false, {
    method: 'PUT',
    body: JSON.stringify(data),
    ...optionsOverride
  }, baseUrlOverride, 'json', sendTokenInHeader)
}

export const remove = (url:string, baseUrlOverride?: string): Promise<any> => {
  return sendRequest(url, false, {method: 'DELETE'}, baseUrlOverride)
}

/**
 * The token's own `exp`/`iat` describe its real lifetime, so prefer them over a hard-coded interval.
 * Returns null when the stored token is not readable, so callers can fall back.
 */
const getTokenLifetime = (): { issuedAt: number, expiresAt: number } | null => {
  try {
    const { iat, exp } = getDecodedToken()

    if (typeof iat !== 'number' || typeof exp !== 'number' || exp <= iat) {
      return null
    }

    return { issuedAt: iat * 1000, expiresAt: exp * 1000 }
  } catch {
    // Nothing stored, or not a readable token, so we cannot derive a lifetime from it.
    return null
  }
}

export function isTokenStale(): boolean {
  // NOTE: seeding `last_refreshed` here makes this read write to storage. Left in place to keep this
  // change focused - see the VFX-3042 pull request.
  let lastRefreshedState = localStorage.getItem(LocalStorageKeys.LAST_REFRESHED)
  const timeNow = Date.now()

  if (!lastRefreshedState) {
    localStorage.setItem(LocalStorageKeys.LAST_REFRESHED, timeNow.toString())
    lastRefreshedState = timeNow.toString()
  }

  const sinceLastRefresh = timeNow - parseInt(lastRefreshedState, 10)

  if (sinceLastRefresh < MINIMUM_REFRESH_INTERVAL) {
    return false
  }

  const lifetime = getTokenLifetime()

  if (lifetime === null) {
    return sinceLastRefresh >= FIVE_MINUTES
  }

  return timeNow >= lifetime.issuedAt + (lifetime.expiresAt - lifetime.issuedAt) * TOKEN_REFRESH_THRESHOLD
}

/** The session cannot be recovered, so drop it and send the user back to login. */
const endSession = (): void => {
  clearTokenData()

  const currentLocation = `${window.location.pathname}${window.location.search}`

  window.location.href = `/v2/login?redirect=${encodeURIComponent(currentLocation)}`
}

const throwRefreshError = async (response: Response): Promise<never> => {
  const errorData = await response.text()
  let error = null
  try {
    error = JSON.parse(errorData)
  } catch {
    // The response text is not a JSON, keep error as null
  }

  const errorMessage = error ? error.detail : errorData
  const authenticateHeader: string = error?.authenticate_header ?? ''
  const isTerminal = response.status === 401 || authenticateHeader.includes(INVALID_TOKEN_AUTH_REFRESH_RESPONSE)

  // A rejected refresh token is not recoverable, so tell every tab by clearing the shared storage.
  if (isTerminal) {
    endSession()
  }

  if (authenticateHeader.includes(INVALID_REQUEST_AUTH_REFRESH_RESPONSE)) {
    throw new InvalidRequestApiError(`Unable to refresh the authentication token: ${errorMessage}`, response.status)
  }

  if (authenticateHeader.includes(INVALID_TOKEN_AUTH_REFRESH_RESPONSE)) {
    throw new InvalidTokenApiError(`Unable to refresh the authentication token: ${errorMessage}`, response.status)
  }

  throw new ApiError(`Unable to refresh the authentication token: ${errorMessage}`, response.status)
}

/**
 * Exchanges the stored refresh token, persisting both tokens before resolving.
 * The refresh token rotates once the backend flag is on; while it is off the response may omit it,
 * in which case the stored one still works and must be kept.
 */
const requestNewToken = async (refreshTokenValue: string, signal?: AbortSignal): Promise<void> => {
  const response = await fetch(`${import.meta.env.VITE_USER_MANAGER_URL}/auth/token/refresh`, {
    method: 'POST',
    headers: {'Content-Type': 'application/json', 'Accept': 'application/json',},
    body: JSON.stringify({refresh_token: refreshTokenValue}),
    signal,
  })

  if (!response.ok) {
    await throwRefreshError(response)
  }

  const { data }: { data: RefreshResponse } = await response.json()

  storeTokenData({ token: data.token, refresh_token: data.refresh_token ?? refreshTokenValue })
}

/**
 * Tokens are shared through localStorage, so two tabs refreshing at once means the loser holds a
 * rotated-away refresh token. Web Locks elects one refreshing tab; the others run the callback
 * afterwards, see the access token has already changed, and skip the network call.
 */
const refreshUnderLock = async (signal: AbortSignal): Promise<void> => {
  const tokenBeforeLock = getToken()

  const refresh = async (): Promise<void> => {
    const refreshTokenValue = getRefreshToken()

    if (refreshTokenValue === null) {
      return
    }

    await requestNewToken(refreshTokenValue, signal)
  }

  // Undefined outside a secure context (including local http development), so degrade to refreshing directly.
  if (!navigator.locks) {
    await refresh()

    return
  }

  // The signal aborts a lock still being waited on; the callback passes it to the fetch so an
  // in-progress exchange is cancelled too, rather than left running after the timeout fires.
  await navigator.locks.request(REFRESH_LOCK_NAME, { signal }, async (): Promise<void> => {
    if (getToken() !== tokenBeforeLock) {
      // Another tab held the lock and has already refreshed for us.
      return
    }

    await refresh()
  })
}

const withTimeout = async (work: (signal: AbortSignal) => Promise<void>, timeout: number): Promise<void> => {
  const controller = new AbortController()
  let timer: ReturnType<typeof setTimeout> | undefined

  try {
    await Promise.race([
      work(controller.signal),
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(
          () => {
            // Abort the in-flight refresh before rejecting, so a timed-out exchange cannot keep
            // running and race a later one over the (rotating) refresh token.
            controller.abort()
            reject(new ApiError('Timed out refreshing the authentication token', 408))
          },
          timeout
        )
      })
    ])
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Single-flight guard. It holds a promise that only settles once the new tokens are persisted, so a
 * follower never returns before the token it is about to read has been written, and every waiter
 * observes the same failure.
 */
let refreshPromise: Promise<void> | null = null

export async function refreshToken(): Promise<void> {
  if (refreshPromise) {
    await refreshPromise

    return
  }

  // Without a refresh token there is nothing to exchange - a 2FA-interim session, for example.
  if (getRefreshToken() === null) {
    return
  }

  refreshPromise = withTimeout(signal => refreshUnderLock(signal), REFRESH_TIMEOUT)
    .finally((): void => {
      refreshPromise = null
    })

  await refreshPromise
}

/** Returns whether the retry is worth making, so a failed refresh surfaces the original 401 instead. */
const refreshForRetry = async (previousToken: string): Promise<boolean> => {
  try {
    await refreshToken()
  } catch {
    // The refresh failed and has already ended the session if that failure was terminal.
    return false
  }

  return getToken() !== previousToken
}

export async function refreshTokenIfStale(): Promise<void> {
  if (!isTokenStale()) {
    return
  }

  await refreshToken()
}